import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Field, FieldVisibilityRuleType as RuleType } from "@/core/types/types";
import useInterpolateText from "../useInterpolateText/useInterpolateText";

const DEFAULT_VISIBLE: boolean = false;
const IS_VISIBLE: boolean = true;
const STRING_REGEX: RegExp = /String\(([^)]+)\)/g;

// Returns true if the field should be visible. The predicate has
// access to values of the other form inputs
// Example rule: { hide-if: "flightSelector != null }
const useFieldVisibility = (field: Field) => {
  // Watching the form values allows us to react on them
  const { watch } = useFormContext();
  const formValues = watch();
  // Make fields hidden by default to avoid form flickering
  const [isVisible, setIsVisible] = useState(DEFAULT_VISIBLE);
  const predicate = field.properties?.[RuleType.HideIf];
  const { interpolatedText, variables } = useInterpolateText({
    text: predicate,
  });
  useEffect(() => {
    // Show field by default if no predication specified
    if (!predicate) return setIsVisible(IS_VISIBLE);

    try {
      const formatedPredict = interpolatedText.replace(STRING_REGEX, "'$1'");

      const doesPass = evaluate(formatedPredict, {
        ...formValues,
        ...variables,
      });

      setIsVisible(!doesPass);
    } catch (error) {
      // TODO: report error, since there might be a bad variable defined in the form
      console.error(error);
      setIsVisible(IS_VISIBLE);
    }
  }, [field.properties, formValues]);

  return isVisible;
};

function evaluate(predicate: string, formValues: {}) {
  // For each form value, initialise a variable with that value
  const variableDeclarations = Object.entries(formValues)
    .map(([key, value]) => {
      let sandboxValue = `"${value}"`;

      if (typeof value === "number" || value === null) {
        sandboxValue = `${value}`;
      }

      if (typeof value === "object" && value !== null) {
        sandboxValue = JSON.stringify(value);
      }

      return `const ${key} = ${sandboxValue};`;
    })
    .join(";");

  const evaluationExpression = `return ${predicate}`;
  const evaluationFunction = `${variableDeclarations};${evaluationExpression};`;

  // Excape evaluation with unresolved variables to avoid errors
  if (predicate.indexOf("${") > -1) return;

  return Function(evaluationFunction)();
}

export { useFieldVisibility };
